/** @fileoverview Bulk-assign an energy source to all KPIs matching active filters. */
import { useMemo } from "react";
import { questionMatchesSteps } from "../Components/TagFilterUtils";
import SourcePicker from "./SourcePicker";

/**
 * Assigns one energy source to every KPI matching the active filter steps.
 * @param {object} props Component props.
 * @param {Array<{id:number,name:string}>} props.sources Available energy sources.
 * @param {Array<{key:string,values:Set<string>}>} props.filterSteps Active filter steps.
 * @param {object[]} props.allQuestions All mappable questions.
 * @param {Record<number, number>} props.kpiSourceMap Per-KPI source assignments.
 * @param {(kpiId:number, sourceId:number|null)=>void} props.onKpiSourceChange Assignment handler.
 * @param {boolean} props.disabled Disable interaction.
 * @returns {JSX.Element|null} The assign bar, or null when not applicable.
 */
export default function SourceAssignBar({ sources, filterSteps, allQuestions, kpiSourceMap, onKpiSourceChange, disabled }) {
  const matchingKpis = useMemo(() => {
    const active = filterSteps.filter(s => s.values?.size);
    if (!active.length) return [];
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [allQuestions, filterSteps]);

  const meta = useMemo(() => {
    if (!matchingKpis.length) return { assigned: 0, total: 0, value: null, isMixed: false };
    const ids = matchingKpis.map(q => kpiSourceMap[q.questionId] || null);
    const assigned = ids.filter(Boolean).length;
    const unique = new Set(ids.filter(Boolean));
    const isMixed = unique.size > 1 || (assigned > 0 && assigned < matchingKpis.length);
    const value = (unique.size === 1 && assigned === matchingKpis.length) ? [...unique][0] : null;
    return { assigned, total: matchingKpis.length, value, isMixed };
  }, [matchingKpis, kpiSourceMap]);

  const activeSteps = filterSteps.filter(s => s.values?.size);
  if (!activeSteps.length || !sources.length || !matchingKpis.length) return null;

  return (
    <div style={{
      marginTop: 10, padding: "8px 14px", borderRadius: 10,
      background: meta.value ? "var(--es-amber-50)" : "var(--es-slate-50)",
      border: `1.5px solid ${meta.value ? "var(--es-amber-300)" : "var(--es-slate-200)"}`,
      display: "flex", alignItems: "center", justifyContent: "space-between"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>⚡</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--es-amber-900)" }}>Assign source to filtered KPIs</div>
          <div style={{ fontSize: 10, color: "var(--es-amber-700)" }}>Updates all {matchingKpis.length} KPIs matching these filters</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {meta.isMixed && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "var(--es-orange-600)", background: "var(--es-orange-50)", padding: "2px 6px", borderRadius: 4, border: "1px solid var(--es-orange-300)" }}>
            Mixed ({meta.assigned}/{meta.total})
          </span>
        )}
        <SourcePicker
          sources={sources}
          value={meta.value}
          disabled={disabled}
          onChange={sourceId => matchingKpis.forEach(q => onKpiSourceChange(q.questionId, sourceId))}
        />
      </div>
    </div>
  );
}
